// SpaceSync — keeps the journals of my devices in step whenever two of them are online.
//
// The devices meet in the space's room (one per key epoch: the current and the previous one, so
// a device that missed a rekey can still be reached and catch up), authenticate with the space
// key (DTLS-bound proof, same code as share links), swap version vectors, send each other what is
// missing, then push every newly accepted envelope — including other devices' (gossip).
//
// Delivery knowledge: devices also pass along the last vector they know for every other device
// ("known"), and acknowledge with their vector shortly after receiving envelopes. So I learn that
// my contact's phone has my message even if it was my other device that handed it over.
//
// Events: 'peers' (connected device count changed), 'synced' {peer}, 'known' (delivery knowledge
// grew), 'seen' {device} (a device is online now).

import { Mesh } from '../core/mesh';
import { spaceAuthKey, spaceRoom } from './keys';
import type { Journal, Vector } from './journal';
import { isEnvelope, type Envelope } from './envelope';

const HIGH_WATER = 1024 * 1024;
const FRAGMENT = 60 * 1024;

type Msg = { t: 'who'; device: string } | { t: 'vec'; vec: Vector; known?: Record<string, Vector> } | { t: 'env'; e: Envelope } | { t: 'frag'; id: string; i: number; n: number; s: string };

export class SpaceSync extends EventTarget {
  readonly #meshes = new Map<number, Mesh>();
  readonly #frags = new Map<string, string[]>();
  /** Which device sits behind each authenticated connection (announced right after auth). */
  readonly #deviceOf = new Map<string, string>();
  /** Last vector known for each device, mine included: who holds what. */
  readonly known = new Map<string, Vector>();
  /** When each device was last connected to one of my devices (ms). */
  readonly lastSeen = new Map<string, number>();
  readonly #ackTimers = new Map<string, ReturnType<typeof setTimeout>>();
  #persistTimer: ReturnType<typeof setTimeout> | undefined;
  #stopped = false;
  #sending = new Map<string, Promise<void>>();

  constructor(
    readonly journal: Journal,
    readonly wsUrl: (path: string) => string,
    readonly iceServers: () => Promise<RTCIceServer[]>,
  ) {
    super();
    journal.addEventListener('envelope', (ev) => {
      const { envelope } = (ev as CustomEvent).detail as { envelope: Envelope };
      this.#broadcast({ t: 'env', e: envelope });
    });
    journal.addEventListener('rekey', () => void this.refreshRooms());
  }

  get peerCount(): number {
    return this.onlineDevices().size;
  }

  onlineDevices(): Set<string> {
    const ids = new Set<string>();
    for (const m of this.#meshes.values()) for (const p of m.authedPeers) ids.add(this.#deviceOf.get(p.id) ?? p.id);
    return ids;
  }

  async start(): Promise<void> {
    this.#stopped = false;
    for (const [d, v] of (await this.journal.store.getKv<[string, Vector][]>('known')) ?? []) this.known.set(d, v);
    for (const [d, t] of (await this.journal.store.getKv<[string, number][]>('seen')) ?? []) this.lastSeen.set(d, t);
    await this.refreshRooms();
  }

  stop(): void {
    this.#stopped = true;
    for (const m of this.#meshes.values()) m.close();
    this.#meshes.clear();
  }

  /** Joins the rooms of the current and previous epochs, leaves older ones. */
  async refreshRooms(): Promise<void> {
    if (this.#stopped) return;
    const { epoch, secrets } = this.journal.space;
    const wanted = [epoch, epoch - 1].filter((e) => secrets[e]);
    for (const [e, mesh] of this.#meshes) {
      if (!wanted.includes(e)) {
        mesh.close();
        this.#meshes.delete(e);
      }
    }
    const ice = await this.iceServers();
    for (const e of wanted) {
      if (this.#meshes.has(e)) continue;
      const mesh = new Mesh({ url: this.wsUrl(`/ws/room/${await spaceRoom(secrets[e])}`), iceServers: ice, authKey: await spaceAuthKey(secrets[e]) });
      this.#meshes.set(e, mesh);
      mesh.addEventListener('authed', (ev) => {
        const { peer } = (ev as CustomEvent).detail;
        void this.#send(mesh, peer, { t: 'who', device: this.journal.device.id });
        void this.#send(mesh, peer, this.#vecMsg());
        this.dispatchEvent(new Event('peers'));
      });
      for (const type of ['down', 'gone']) {
        mesh.addEventListener(type, (ev) => {
          const device = this.#deviceOf.get((ev as CustomEvent).detail.peer);
          if (device) this.#seen(device);
          if (type === 'gone') this.#deviceOf.delete((ev as CustomEvent).detail.peer);
          this.dispatchEvent(new Event('peers'));
        });
      }
      mesh.addEventListener('ctl', (ev) => {
        const { peer, msg } = (ev as CustomEvent).detail;
        void this.#onMessage(mesh, peer, msg);
      });
      // Not awaited: one unreachable room must not hold back the others; sync is event-driven.
      void mesh.start();
    }
  }

  async #onMessage(mesh: Mesh, peer: string, raw: unknown): Promise<void> {
    const msg = raw as Msg;
    if (msg?.t === 'frag' && typeof msg.s === 'string' && Number.isInteger(msg.i) && Number.isInteger(msg.n) && msg.n < 200) {
      const key = `${peer}:${msg.id}`;
      const parts = this.#frags.get(key) ?? new Array<string>(msg.n);
      parts[msg.i] = msg.s;
      this.#frags.set(key, parts);
      if (parts.filter((p) => p !== undefined).length === msg.n) {
        this.#frags.delete(key);
        try {
          await this.#onMessage(mesh, peer, JSON.parse(parts.join('')));
        } catch {
          // Malformed reassembly: dropped.
        }
      }
      return;
    }
    if (msg?.t === 'who' && typeof msg.device === 'string' && /^[0-9a-f]{32}$/.test(msg.device)) {
      this.#deviceOf.set(peer, msg.device);
      this.#seen(msg.device);
      this.dispatchEvent(new Event('peers'));
      return;
    }
    if (msg?.t === 'vec' && msg.vec && typeof msg.vec === 'object') {
      const device = this.#deviceOf.get(peer);
      let grew = device ? this.#merge(device, msg.vec) : false;
      if (msg.known && typeof msg.known === 'object') {
        for (const [d, v] of Object.entries(msg.known).slice(0, 64)) if (/^[0-9a-f]{32}$/.test(d) && v && typeof v === 'object') grew = this.#merge(d, v) || grew;
      }
      if (grew) this.#persist();
      for (const e of await this.journal.missingFor(msg.vec)) await this.#send(mesh, peer, { t: 'env', e });
      this.dispatchEvent(new CustomEvent('synced', { detail: { peer } }));
      return;
    }
    if (msg?.t === 'env' && isEnvelope(msg.e)) {
      if ((await this.journal.ingest(msg.e)) === 'ok') this.#scheduleAck(mesh, peer);
    }
  }

  #vecMsg(): Msg {
    const vec = this.journal.vector();
    this.known.set(this.journal.device.id, vec);
    return { t: 'vec', vec, known: Object.fromEntries(this.known) };
  }

  /** Tells the sender what we now hold, so its "delivered" marks update without waiting. */
  #scheduleAck(mesh: Mesh, peer: string): void {
    clearTimeout(this.#ackTimers.get(peer));
    this.#ackTimers.set(peer, setTimeout(() => void this.#send(mesh, peer, this.#vecMsg()), 300));
  }

  /** Component-wise max into known[device]; true if anything grew. */
  #merge(device: string, vec: Vector): boolean {
    const cur = { ...(this.known.get(device) ?? {}) };
    let grew = false;
    for (const [author, seq] of Object.entries(vec)) {
      if (typeof seq === 'number' && Number.isSafeInteger(seq) && seq > (cur[author] ?? 0)) {
        cur[author] = seq;
        grew = true;
      }
    }
    if (grew) this.known.set(device, cur);
    return grew;
  }

  #seen(device: string): void {
    this.lastSeen.set(device, Date.now());
    this.dispatchEvent(new CustomEvent('seen', { detail: { device } }));
    this.#persist();
  }

  #persist(): void {
    this.dispatchEvent(new Event('known'));
    clearTimeout(this.#persistTimer);
    this.#persistTimer = setTimeout(() => {
      void this.journal.store.setKv('known', [...this.known]);
      void this.journal.store.setKv('seen', [...this.lastSeen]);
    }, 500);
  }

  /** Whether a device holds envelope number seq of author, as far as we know. */
  holds(device: string, author: string, seq: number): boolean {
    return (this.known.get(device)?.[author] ?? 0) >= seq;
  }

  /**
   * Every accepted envelope goes to every connected device, including the one it came from:
   * that one answers 'dup' and stops there, so gossip converges without tracking sources.
   */
  #broadcast(msg: Msg): void {
    for (const mesh of this.#meshes.values()) for (const p of mesh.authedPeers) void this.#send(mesh, p.id, msg);
  }

  /** Per-peer ordered queue with backpressure on the control channel; big messages fragmented. */
  #send(mesh: Mesh, peer: string, msg: Msg): Promise<void> {
    const key = `${peer}`;
    const run = (this.#sending.get(key) ?? Promise.resolve()).then(async () => {
      const text = JSON.stringify(msg);
      const pieces = text.length <= FRAGMENT ? [text] : chunk(text, FRAGMENT);
      const id = Math.random().toString(36).slice(2, 10);
      for (const [i, s] of pieces.entries()) {
        const ctl = mesh.peers.get(peer)?.session.ctl;
        if (!ctl || ctl.readyState !== 'open') return;
        while (ctl.bufferedAmount > HIGH_WATER && ctl.readyState === 'open') await new Promise((r) => setTimeout(r, 50));
        mesh.send(peer, pieces.length === 1 ? msg : { t: 'frag', id, i, n: pieces.length, s });
      }
    });
    this.#sending.set(key, run.catch(() => {}));
    return run;
  }
}

function chunk(text: string, size: number): string[] {
  const out: string[] = [];
  for (let i = 0; i < text.length; i += size) out.push(text.slice(i, i + size));
  return out;
}
